import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import { finalize, take, takeUntil } from 'rxjs';

import { CoreService, DataService, DialogService, FormNavigationService, SaveDataRequest, SpinnerService } from 'dspx-core';

import { API_ENDPOINT_KEYS, API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import {
  APP_DATA_KEYS,
  BREADCRUMBS,
  FORM_IDENTIFIERS,
  NAV_TAB_NUMBERS,
  NoAccessRuleKey,
  PAGE_NUMBERS,
  POP_UP_CODES,
  QUESTION_CODES,
  QUESTION_PATHS,
  READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS,
  SAVE_RESULT_STATUS,
  SERVER_ERROR_POP_UP_CODES
} from '../../constants/app-data.constants';
import { DASHBOARD_FILTER_NAMES } from '../../constants/grid-data-filter.constants';

import { MaatJobItemGridFilterRequest } from '../../interfaces/maat-job-item-grid-filter-request.interface';
import { MaatNcqGridFilterRequest } from '../../interfaces/maat-ncq-grid-filter-request.interface';
import { MaatPropertyHistoryGridFilterRequest } from '../../interfaces/maat-property-history-grid-filter-request.interface';
import { MaatJobSaveDataRequest } from '../../interfaces/maat-job-save-data-request.interface';
import { LoggedInUserRoleFlags } from '../../interfaces/logged-in-user-role-flags.interface';
import { LoggedInUserProfile } from '../../interfaces/logged-in-user-profile.interface';

import { DateTimeValidator } from '../../helpers/date-time-validator';
import { FileUploadHelper } from '../../helpers/file-upload-helper';
import { MaatAuditPlanAccess } from '../../helpers/maat-audit-plan-access';
import { MaatJobAccess } from '../../helpers/maat-job-access';
import { PagePath } from '../../helpers/config-data-parser';
import { MaatNcqAccess } from '../../helpers/maat-ncq-access';
import { GridDataFilterHandler } from '../../helpers/grid-data-filter-handler';
import { GridDataClientHandler } from '../../helpers/grid-data-client-handler';

import { ServiceWorkerService } from '../../services/service-worker.service';
import { AppDataService } from '../../services/app-data.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { FormConfigurationParserService } from '../../services/form-configuration-parser.service';
import { ErrorService } from '../../services/error.service';
import { HtmlHelperService } from '../../services/html-helper.service';

import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-maat-job-item-list',
  templateUrl: './maat-job-item-list.component.html',
  styleUrls: ['./maat-job-item-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MaatJobItemListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {

  private loggedInUserProfile: LoggedInUserProfile;
  private maatJobItemFilterHandler: GridDataFilterHandler<MaatJobItemGridFilterRequest>;
  private maatNcqFilterHandler: GridDataFilterHandler<MaatNcqGridFilterRequest>;
  private maatPropertyHistoryFilterHandler: GridDataFilterHandler<MaatPropertyHistoryGridFilterRequest>;

  private maatJobItemGridDataClientHandler: GridDataClientHandler;
  private maatNcqGridDataClientHandler: GridDataClientHandler;
  private maatPropertyHistoryGridDataClientHandler: GridDataClientHandler;

  activeDialog: string = '';
  maatAuditPlanId: string = '';
  maatJobId: string = '';

  constructor(
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService,
    private route: ActivatedRoute,
    private coreService: CoreService,
    private formConfigurationParserService: FormConfigurationParserService,
    private dataService: DataService,
    private errorService: ErrorService) {

    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);

    this.initialise(
      TEMPLATES.maatJobItemList,
      null,
      true);

    this.initialiseReturnNavigationState();

    this.maatJobItemFilterHandler = new GridDataFilterHandler<MaatJobItemGridFilterRequest>(
      this.responseParserService,
      this.appDataService,
      () => this.baseForm.model,
      {
        getLoggedInUserEmail: () => this.loggedInUserProfile?.emailAddress,
        getFilterName: () => DASHBOARD_FILTER_NAMES.maatJobItemsByJob(this.maatJobId),
        searchQuestionPath: [...QUESTION_PATHS.maatJobItemList.search],
        filterQuestionPath: null,
        getFilterRequestFromModels: (searchQuestionModel: any, filterQuestionModel: any) => ({
          searchTerm: searchQuestionModel,
          navTabIndex: NAV_TAB_NUMBERS.maatJobItemList.maatJobItems
        }),
        getDefaultFilterRequest: () => ({
          navTabIndex: NAV_TAB_NUMBERS.maatJobItemList.maatJobItems
        })
      });

    this.maatNcqFilterHandler = new GridDataFilterHandler<MaatNcqGridFilterRequest>(
      this.responseParserService,
      this.appDataService,
      () => this.baseForm.model,
      {
        getLoggedInUserEmail: () => this.loggedInUserProfile?.emailAddress,
        getFilterName: () => DASHBOARD_FILTER_NAMES.maatNcqsByJob(this.maatJobId),
        searchQuestionPath: [...QUESTION_PATHS.maatJobItemList.ncqListSearch],
        filterQuestionPath: [...QUESTION_PATHS.maatJobItemList.ncqListFilter],
        getFilterRequestFromModels: (searchQuestionModel: any, filterQuestionModel: any) => ({
          searchTerm: searchQuestionModel,
          ncqStatuses: filterQuestionModel?.ncqStatuses,
          category: filterQuestionModel?.category,
          headContractorPosition: filterQuestionModel?.headContractorPosition,
          businessPosition: filterQuestionModel?.businessPosition,
          tradeCode: filterQuestionModel?.tradeCode,
          responseRequiredByFrom: filterQuestionModel?.responseRequiredByFrom,
          responseRequiredByTo: filterQuestionModel?.responseRequiredByTo,
          navTabIndex: NAV_TAB_NUMBERS.maatJobItemList.maatNcqs
        }),
        getDefaultFilterRequest: () => ({
          ncqStatuses: this.getMaatNcqAccess().getDefaultNcqStatuses(),
          navTabIndex: NAV_TAB_NUMBERS.maatJobItemList.maatNcqs
        })
      });

    this.maatPropertyHistoryFilterHandler = new GridDataFilterHandler<MaatPropertyHistoryGridFilterRequest>(
      this.responseParserService,
      this.appDataService,
      () => this.baseForm.model,
      {
        getLoggedInUserEmail: () => this.loggedInUserProfile?.emailAddress,
        getFilterName: () => DASHBOARD_FILTER_NAMES.maatPropertyHistoriesByJob(this.maatJobId),
        searchQuestionPath: [...QUESTION_PATHS.maatJobItemList.maatPropertyHistorySearch],
        filterQuestionPath: null,
        getFilterRequestFromModels: (searchQuestionModel: any, filterQuestionModel: any) => ({
          searchTerm: searchQuestionModel,
          navTabIndex: NAV_TAB_NUMBERS.maatJobItemList.maatPropertyHistories
        }),
        getDefaultFilterRequest: () => ({
          navTabIndex: NAV_TAB_NUMBERS.maatJobItemList.maatPropertyHistories
        })
      });

    this.maatJobItemGridDataClientHandler = new GridDataClientHandler(
      this.appDataService,
      () => this.formConfigurationParserService.findQuestionConfig(this.baseForm.fields, ...QUESTION_PATHS.maatJobItemList.placeholder),
      () => this.loggedInUserProfile?.emailAddress,
      () => DASHBOARD_FILTER_NAMES.maatJobItemsByJob(this.maatJobId));

    this.maatNcqGridDataClientHandler = new GridDataClientHandler(
      this.appDataService,
      () => this.formConfigurationParserService.findQuestionConfig(this.baseForm.fields, ...QUESTION_PATHS.maatJobItemList.ncqListPlaceholder),
      () => this.loggedInUserProfile?.emailAddress,
      () => DASHBOARD_FILTER_NAMES.maatNcqsByJob(this.maatJobId));

    this.maatPropertyHistoryGridDataClientHandler = new GridDataClientHandler(
      this.appDataService,
      () => this.formConfigurationParserService.findQuestionConfig(this.baseForm.fields, ...QUESTION_PATHS.maatJobItemList.maatPropertyHistoryPlaceholder),
      () => this.loggedInUserProfile?.emailAddress,
      () => DASHBOARD_FILTER_NAMES.maatPropertyHistoriesByJob(this.maatJobId));
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap: ParamMap) => {
      this.maatAuditPlanId = paramMap.get('maatAuditPlanId');
      this.maatJobId = paramMap.get('maatJobId');
      if (this.maatJobId) {
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getMaatJobConfigAndResponse;
      }
    });

    this.coreService.conditionFunctions['isAlwaysTrue'] = () => true;
    this.coreService.conditionFunctions['isReadOnly'] = () => !this.canEditJob();
    this.coreService.conditionFunctions['isInspectionDateReadOnly'] = () => this.isInspectionDateReadOnly();

    this.coreService.applicationFunctions['validateInspectionDate'] = (args: any[]) =>
      DateTimeValidator.validateDateFormat(args);

    this.overrideHtmlTitleSetByDspxFrameworkOnFormNavigation();

    this.waitForServiceWorker().then(waitResult => {

      this.loggedInUserProfile = waitResult.loggedInUserProfile;

      this.maatJobItemFilterHandler.setInitialFilterRequest();
      this.maatNcqFilterHandler.setInitialFilterRequest();
      if (this.getMaatJobAccess().canSeePropertyHistory()) {
        this.maatPropertyHistoryFilterHandler.setInitialFilterRequest();
      }
    });
  }

  ngOnDestroy(): void {

    this.maatJobItemGridDataClientHandler.unsubscribe();
    this.maatNcqGridDataClientHandler.unsubscribe();
    this.maatPropertyHistoryGridDataClientHandler.unsubscribe();

    this.completeDestroy();
  }

  cancel(event: any): void {
    this.navigate(['/', ROUTE_CONSTANTS.maatJob.listPrefix, this.maatAuditPlanId]);
  }

  formLoad(event: any): void {
    const parentId = this.maatJobId || '';
    const additionalData = event?.customConfigData?.[parentId] ?? {};
    const { maatAuditPlanHeading, maatJobHeading } = additionalData;
    this.appDataService.setBreadcrumbs([
      { label: BREADCRUMBS.pageHeadings.auditPlans, url: ROUTE_CONSTANTS.maatAuditPlan.list },
      { label: maatAuditPlanHeading, url: `${ROUTE_CONSTANTS.maatJob.listPrefix}/${this.maatAuditPlanId}` },
      { label: maatJobHeading }
    ]);

    this.configData = event.configData;
    // On the form load event the base form model is populated with the job details, but the model remains undefined.
    // We set the model to the base form model to ensure that job details are populated along with the grid data.
    this.model = this.baseForm.model;

    this.maatJobItemGridDataClientHandler.copyConfigurationFromStoreIntoGrid()
      .then(() => this.maatJobItemGridDataClientHandler.subscribe())
      .then(() => this.loadJobItemsGridData(() => this.maatJobItemFilterHandler.getInitialFilterRequest()));

    this.maatNcqGridDataClientHandler.copyConfigurationFromStoreIntoGrid()
      .then(() => this.maatNcqGridDataClientHandler.subscribe())
      .then(() => this.loadNcqsGridData(() => this.maatNcqFilterHandler.getInitialFilterRequest()));

    this.maatPropertyHistoryGridDataClientHandler.copyConfigurationFromStoreIntoGrid()
      .then(() => this.maatPropertyHistoryGridDataClientHandler.subscribe())
      .then(() => this.loadPropertyHistoryGridData(() => this.maatPropertyHistoryFilterHandler.getInitialFilterRequest()));

    this.setAllTabsHeading(maatJobHeading);

    this.fileUploadQuestions = FileUploadHelper.getFileUploadQuestionGetModelFunctions(this.formConfigurationParserService, this.responseParserService, this.baseForm);
    this.coreService.fileUploadFormIdentifiers[QUESTION_CODES.maatJobItemList.documentation] = FORM_IDENTIFIERS.maatJob.documentation;
    this.coreService.fileUploadFormIdentifiers[QUESTION_CODES.maatJobItemList.contractorDocumentation] = FORM_IDENTIFIERS.maatJob.contractorDocumentation;

    this.modelKeysToIgnore = [
      QUESTION_CODES.maatJobItemList.maatJobItemPlaceholder,
      QUESTION_CODES.maatJobItemList.maatNcqPlaceholder,
      QUESTION_CODES.maatJobItemList.maatPropertyHistoryPlaceholder,
      QUESTION_CODES.maatJobItemList.maatJobItemSearch,
      QUESTION_CODES.maatJobItemList.maatNcqSearch,
      QUESTION_CODES.maatJobItemList.maatNcqFilter,
      QUESTION_CODES.maatJobItemList.maatPropertyHistorySearch
    ]
    this.setPreviousModel({ isAsync: true });

    const pagePaths: PagePath[] = [
      [NAV_TAB_NUMBERS.maatJobItemList.maatJob, PAGE_NUMBERS.maatJobItemList.maatJob],
      [NAV_TAB_NUMBERS.maatJobItemList.maatJobItems, PAGE_NUMBERS.maatJobItemList.maatJobItems],
      [NAV_TAB_NUMBERS.maatJobItemList.maatNcqs, PAGE_NUMBERS.maatJobItemList.maatNcqs]
    ];
    this.ensurePageActionButtonsAreEnabledOnFormLoad(pagePaths, READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS.maatJobItemList);

    this.initialiseQuestionModel(NoAccessRuleKey, this.getNoAccessFlagValue());

    this.restoreReturnActiveTab();
  }

  modelChanged(event: any): void {
    const noAccess = this.getNoAccessFlagValue();
    this.handleQuestionChange(
      () => noAccess,
      () => this.showNoAccessConfirmMessage(noAccess),
      NoAccessRuleKey
    );
  }

  save(event: any): void {
    const saveDataRequest: MaatJobSaveDataRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.maatJobId,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: false,
      customSaveData: {
        parentId: this.maatAuditPlanId
      }
    }
    if (!event.isFormValid) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showPopup(POP_UP_CODES.common.validationError);
      }
      else {
        this.saveData(saveDataRequest);
      }
    }
    else {
      this.saveData(saveDataRequest);
    }
  }

  private getNoAccessFlagValue(): boolean {
    return this.getQuestionValue(this.baseForm.model, ...QUESTION_PATHS.maatJobItemList.isNoAccess) ?? false;
  }

  private loadJobItemsGridData(getfilterRequest = this.maatJobItemFilterHandler.getFilterRequest): void {
    const onGridLoaded = () => this.maatJobItemGridDataClientHandler.copyConfigurationFromStoreIntoGrid();
    this.loadGridData(getfilterRequest(), onGridLoaded, NAV_TAB_NUMBERS.maatJobItemList.maatJobItems, `${API_ENDPOINTS.dashboard.getMaatJobItems}/${this.maatJobId}`);
  }

  private loadNcqsGridData(getfilterRequest = this.maatNcqFilterHandler.getFilterRequest): void {
    const onGridLoaded = () => this.maatNcqGridDataClientHandler.copyConfigurationFromStoreIntoGrid();
    this.loadGridData(getfilterRequest(), onGridLoaded, NAV_TAB_NUMBERS.maatJobItemList.maatNcqs, `${API_ENDPOINTS.dashboard.getMaatNcqsByJob}/${this.maatJobId}`);
  }

  private loadPropertyHistoryGridData(getfilterRequest = this.maatPropertyHistoryFilterHandler.getFilterRequest): void {
    if (this.getMaatJobAccess().canSeePropertyHistory()) {
      const onGridLoaded = () => this.maatPropertyHistoryGridDataClientHandler.copyConfigurationFromStoreIntoGrid();
      this.loadGridData(getfilterRequest(), onGridLoaded, NAV_TAB_NUMBERS.maatJobItemList.maatPropertyHistories, `${API_ENDPOINTS.dashboard.getMaatPropertyHistoryByJob}/${this.maatJobId}`);
    }
  }

  private setAllTabsHeading(heading: string): void {
    const pageConfigs = [
      this.formConfigurationParserService.findPageConfig(this.baseForm.fields, NAV_TAB_NUMBERS.maatJobItemList.maatJob, PAGE_NUMBERS.maatJobItemList.maatJob),
      this.formConfigurationParserService.findPageConfig(this.baseForm.fields, NAV_TAB_NUMBERS.maatJobItemList.maatJobItems, PAGE_NUMBERS.maatJobItemList.maatJobItems),
      this.formConfigurationParserService.findPageConfig(this.baseForm.fields, NAV_TAB_NUMBERS.maatJobItemList.maatNcqs, PAGE_NUMBERS.maatJobItemList.maatNcqs),
      this.formConfigurationParserService.findPageConfig(this.baseForm.fields, NAV_TAB_NUMBERS.maatJobItemList.maatPropertyHistories, PAGE_NUMBERS.maatJobItemList.maatPropertyHistories)
    ]
      .filter(pageConfig => !!pageConfig) // pages might be missing depending on the logged-in user role

    pageConfigs.forEach(pageConfig => {
      pageConfig.props.heading = heading;
    });
  }

  private canEditJob(): boolean {
    return this.getMaatJobAccess().canEdit();
  }

  private isInspectionDateReadOnly(): boolean {
    return !this.getMaatAuditPlanAccess().canSetInspectionDate();
  }

  private getMaatAuditPlanAccess(): MaatAuditPlanAccess {
    return new MaatAuditPlanAccess(
      this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags),
      this.getQuestionValue(this.model, ...QUESTION_PATHS.maatJobItemList.planStatus));
  }

  private getMaatJobAccess(): MaatJobAccess {
    return new MaatJobAccess(
      this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags),
      this.getQuestionValue(this.model, ...QUESTION_PATHS.maatJobItemList.planStatus));
  }

  private getMaatNcqAccess(): MaatNcqAccess {
    return new MaatNcqAccess(
      this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags));
  }

  filterData(event: any): void {
    const searchKey = event?.field?.key;

    switch (searchKey) {
      case QUESTION_CODES.maatJobItemList.maatJobItemSearch:
        this.loadJobItemsGridData(() => this.maatJobItemFilterHandler.saveFilterRequest(event.action));
        break;
      case QUESTION_CODES.maatJobItemList.maatNcqSearch:
      case QUESTION_CODES.maatJobItemList.maatNcqFilter:
        this.loadNcqsGridData(() => this.maatNcqFilterHandler.saveFilterRequest(event.action));
        break;
      case QUESTION_CODES.maatJobItemList.maatPropertyHistorySearch:
        this.loadPropertyHistoryGridData(() => this.maatPropertyHistoryFilterHandler.saveFilterRequest(event.action));
        break;
      default:
        throw new Error(`Unknown search key: ${searchKey}`);
    }
  }

  private saveData(request: SaveDataRequest): void {
    this.spinnerService.showSpinner();
    this.dataService.saveData(API_ENDPOINTS.maatJob.save, request)
      .pipe(take(1),
        takeUntil(this.destroy$),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe(data => {
        if (data?.status === SAVE_RESULT_STATUS.common.success) {
          if (SERVER_ERROR_POP_UP_CODES.saveMaatJob.includes(data.message)) {
            this.activeDialog = data.message;
          }
          else {
            this.maatJobId = data.id;
            this.baseForm.lastUpdatedTime = data.allocatedLastUpdatedTime;
            this.activeDialog = POP_UP_CODES.common.saveSuccessful;
          }
          this.dialogService.showPopup(this.activeDialog);
        }
      });
  }

  private showNoAccessConfirmMessage(flag: boolean): void {
    this.activeDialog = flag
      ? POP_UP_CODES.maatJobItemList.noAccessFlagged
      : POP_UP_CODES.maatJobItemList.noAccessUnflagged;

    this.dialogService.showPopup(this.activeDialog);
  }

  actionButton_maatJobItemPlaceholder_datagrid_details_Clicked(event: any): void {
    const maatJobItemId = event.event.data.id;
    this.navigate(['/', ROUTE_CONSTANTS.maatNcq.listPrefix, this.maatAuditPlanId, this.maatJobId, maatJobItemId]);
  }

  actionButton_maatNcqPlaceholder_datagrid_details_Clicked(event: any): void {
    const maatNcqId = event.event.data.id;
    const maatJobItemId = event.event.data.maatJobItemId;
    this.navigate(
      ['/', ROUTE_CONSTANTS.maatNcq.prefix, this.maatAuditPlanId, this.maatJobId, maatJobItemId, maatNcqId],
      false,
      {
        state: {
          returnRoute: ['/', ROUTE_CONSTANTS.maatJobItem.listPrefix, this.maatAuditPlanId, this.maatJobId],
          activeTab: NAV_TAB_NUMBERS.maatJobItemList.maatNcqs
        }
      }
    );
  }

  dialogButton_saveSuccessful_Clicked(event: any): void {
    this.navigate(
      ['/', ROUTE_CONSTANTS.maatJob.listPrefix, this.maatAuditPlanId],
      true,
      {
        state: {
          returnRoute: ['/', ROUTE_CONSTANTS.maatJob.listPrefix, this.maatAuditPlanId],
          activeTab: NAV_TAB_NUMBERS.maatJobList.maatJobs
        }
      }
    );
  }

  dialogButton_noAccessFlagged_Clicked(event: any): void {
    // handle OK button
  }

  dialogButton_noAccessUnflagged_Clicked(event: any): void {
    // handle OK button
  }
}
